import { get } from 'svelte/store';
import { storyStore } from '../stores/storyStore.js';
import { authStore } from '../stores/authStore';

const currentStory = get(storyStore);
const slides = currentStory.slides || [];
const canvasState = slides[0]?.canvasState; // Access the canvas state of the first slide
console.log('Canvas State:', canvasState);

// Constants for Google API scopes
// const SCOPES = 'https://www.googleapis.com/auth/presentations https://www.googleapis.com/auth/drive.file';

/**
 * Initializes the Google API client and checks authorization
 * @returns {Promise<boolean>} Whether the API is initialized and authorized
 */
export async function initGoogleSlidesAPI() {
    if (!process.env.GOOGLE_API_KEY || !process.env.GOOGLE_CLIENT_ID) {
        console.error('Missing Google API credentials in environment variables');
        return false;
    }

    try {
        console.log('Loading Google Identity Services script...');
        await loadGoogleIdentityScript();

        console.log('Loading Google API client library...');
        await loadGapiScript();

        console.log('Initializing Google API client...');
        await window.gapi.client.init({
            discoveryDocs: ['https://slides.googleapis.com/$discovery/rest?version=v1']
        });

        console.log('Google API client initialized successfully');
        return true;
    } catch (error) {
        console.error('Error initializing Google API:', error);
        return false;
    }
}

// Function to load the Google Identity Services script
async function loadGoogleIdentityScript() {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

// Function to load the Google API client library
async function loadGapiScript() {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            // Load the client library after the script loads
            window.gapi.load('client', resolve);
        };
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

/**
 * Checks if the user is signed in and authorized for Slides
 * @returns {Promise<boolean>} Whether the user is signed in
 */
export function isSignedInToGoogle() {
    return !!window.gapi.client.getToken();
}

/**
 * Prompts the user to sign in to Google
 * @returns {Promise<boolean>} Whether sign-in was successful
 */
let authInProgress = false;

/**
 * Checks if the user has Slides-specific permissions
 * @returns {Promise<boolean>} Whether the user has Slides permissions
 */
export async function hasSlidesPermissions() {
    try {
        const response = await fetch('/api/google/verify', {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) return false;

        const data = await response.json();
        return data.success && data.accessToken;
    } catch (error) {
        console.error('Error checking Slides permissions:', error);
        return false;
    }
}

export async function signInToGoogle() {
    try {
        // First check if we already have valid Google tokens
        const verifyResponse = await fetch('/api/google/verify', {
            credentials: 'include' // Important for session cookies
        });
        const verifyData = await verifyResponse.json();
        
        if (verifyData.success) {
            console.log('Using existing Google authorization');
            // Set the token in the Google API client
            gapi.client.setToken({ access_token: verifyData.accessToken });
            return true;
        }
        
        // If no valid token or refresh failed, proceed with new authentication
        let userEmail;
        
        // Try to get email from Google API client first
        try {
            const authInstance = gapi.auth2.getAuthInstance();
            if (authInstance.isSignedIn.get()) {
                const googleUser = authInstance.currentUser.get();
                const profile = googleUser.getBasicProfile();
                userEmail = profile.getEmail();
            }
        } catch (error) {
            console.log('Could not get email from Google API client:', error);
        }
        
        // If no email from Google API, try auth store
        if (!userEmail) {
            const authState = get(authStore);
            userEmail = authState?.user?.email;
        }
        
        // Construct auth URL with parameters to streamline the process
        const authUrl = `/api/google/drive?${new URLSearchParams({
            login_hint: userEmail || '',
            prompt: userEmail ? 'consent' : 'select_account', // Only force consent if we have the email
            scope: 'presentations drive.file'
        })}`;
        
        // Open popup for auth
        const popup = window.open(
            authUrl,
            'google-auth',
            'width=500,height=600,menubar=no,toolbar=no,location=no,status=no'
        );
        
        if (!popup) {
            throw new Error('Failed to open authentication popup. Please allow popups for this site.');
        }
        
        // Listen for auth success message
        return new Promise((resolve, reject) => {
            const messageHandler = async (event) => {
                if (event.data === 'authentication-successful') {
                    window.removeEventListener('message', messageHandler);
                    
                    // Verify the new tokens after successful authentication
                    try {
                        const verifyResponse = await fetch('/api/google/verify', {
                            credentials: 'include'
                        });
                        const verifyData = await verifyResponse.json();
                        
                        if (verifyData.success) {
                            gapi.client.setToken({ access_token: verifyData.accessToken });
                            resolve(true);
                        } else {
                            reject(new Error('Failed to verify new Google authorization'));
                        }
                    } catch (error) {
                        reject(error);
                    }
                }
            };
            
            window.addEventListener('message', messageHandler);
            
            // Check if popup was closed before auth completed
            const checkClosed = setInterval(() => {
                if (popup.closed) {
                    clearInterval(checkClosed);
                    window.removeEventListener('message', messageHandler);
                    reject(new Error('Authentication window was closed'));
                }
            }, 1000);
        });
    } catch (error) {
        console.error('Error during Google authentication:', error);
        throw error;
    }
}

/**
 * Creates a Google Slides presentation from the current story
 * @returns {Promise<{success: boolean, presentationUrl: string|null, error: string|null}>}
 */
export async function exportToGoogleSlides() {
    const currentStory = get(storyStore);
    const title = currentStory.title || 'Untitled Story';
    const slides = currentStory.slides || [];

    if (!slides.length) {
        return {
            success: false,
            presentationUrl: null,
            error: 'No slides to export'
        };
    }

    try {
        console.log('Starting Google Slides export...');

        // Ensure Google API is initialized
        if (!window.gapi?.client) {
            console.error('Google API client is not initialized');
            await initGoogleSlidesAPI();
        }

        // Ensure the user is authenticated
        const isAuthenticated = await ensureGoogleAuthentication();
        if (!isAuthenticated) {
            return {
                success: false,
                presentationUrl: null,
                error: 'User authentication failed'
            };
        }

        console.log('Creating Google Slides presentation...');
        const presentation = await window.gapi.client.slides.presentations.create({
            title
        });

        console.log('Presentation created:', presentation);

        const presentationId = presentation.result.presentationId;

        for (let i = 0; i < slides.length; i++) {
            const slideId = `slide_${i}`;
            const canvasState = slides[i].canvasState;

            if (!canvasState) {
                throw new Error(`Canvas state is missing for slide ${i}`);
            }

            const formattedCanvasState = { objects: canvasState };

            const requests = [
                {
                    createSlide: {
                        objectId: slideId,
                        insertionIndex: i,
                        slideLayoutReference: { predefinedLayout: 'BLANK' }
                    }
                },
                ...generateSlideRequests(formattedCanvasState, slideId)
            ];

            console.log(`Requests for Slide ${i}:`, JSON.stringify(requests, null, 2));

            await window.gapi.client.slides.presentations.batchUpdate({
                presentationId,
                requests
            });
        }

        console.log('Google Slides export successful');
        return {
            success: true,
            presentationUrl: `https://docs.google.com/presentation/d/${presentationId}/edit`,
            presentationId: presentationId,
            error: null
        };
    } catch (error) {
        console.error('Error exporting to Google Slides:', error);
        return {
            success: false,
            presentationUrl: null,
            error: error.message || 'Failed to export to Google Slides'
        };
    }
}

/**
 * Creates a Google Slides presentation with the provided data
 * @param {Object} data - The data to use for creating the slides
 * @returns {Promise<Object>} - Result of the operation
 */
export async function createGoogleSlides(data) {
    try {
        // Check if we're signed in to Google
        const isSignedIn = await isSignedInToGoogle();
        if (!isSignedIn) {
            // Try to sign in
            const signInResult = await signInToGoogle();
            if (!signInResult) {
                throw new Error('Failed to sign in to Google');
            }
        }
        
        // Create a new presentation
        const response = await window.gapi.client.slides.presentations.create({
            title: data.title || 'My Presentation'
        });
        
        const presentationId = response.result.presentationId;
        
        // Add slides based on the data
        if (data.slides && data.slides.length > 0) {
            // Create requests for each slide
            const requests = data.slides.map((slide, index) => {
                return {
                    createSlide: {
                        objectId: `slide_${index}`,
                        insertionIndex: index,
                        slideLayoutReference: {
                            predefinedLayout: 'TITLE_AND_BODY'
                        }
                    }
                };
            });
            
            // Execute the requests
            await window.gapi.client.slides.presentations.batchUpdate({
                presentationId: presentationId,
                requests: requests
            });
            
            // Add content to each slide
            for (let i = 0; i < data.slides.length; i++) {
                const slide = data.slides[i];
                const slideId = `slide_${i}`;
                
                // Add title
                if (slide.title) {
                    await window.gapi.client.slides.presentations.batchUpdate({
                        presentationId: presentationId,
                        requests: [{
                            updateTextStyle: {
                                objectId: slideId,
                                textRange: {
                                    type: 'ALL'
                                },
                                fields: 'fontSize',
                                textStyle: {
                                    fontSize: {
                                        magnitude: 24,
                                        unit: 'PT'
                                    }
                                }
                            }
                        }]
                    });
                    
                    await window.gapi.client.slides.presentations.batchUpdate({
                        presentationId: presentationId,
                        requests: [{
                            replaceAllText: {
                                containsText: {
                                    text: 'Title',
                                    matchCase: true
                                },
                                replaceText: slide.title,
                                pageObjectIds: [slideId]
                            }
                        }]
                    });
                }
                
                // Add body content
                if (slide.content) {
                    await window.gapi.client.slides.presentations.batchUpdate({
                        presentationId: presentationId,
                        requests: [{
                            replaceAllText: {
                                containsText: {
                                    text: 'Subtitle',
                                    matchCase: true
                                },
                                replaceText: slide.content,
                                pageObjectIds: [slideId]
                            }
                        }]
                    });
                }
            }
        }
        
        // Return the presentation ID and a link to view it
        return {
            success: true,
            presentationId: presentationId,
            viewUrl: `https://docs.google.com/presentation/d/${presentationId}/edit`
        };
    } catch (error) {
        console.error('Error creating Google Slides:', error);
        return {
            success: false,
            error: error.message || 'Failed to create Google Slides presentation'
        };
    }
}

// Function to create a compressed version of an external image
// async function compressExternalImage(imageUrl) {
//     try {
//         // Create an image element to load the image
//         const img = new Image();
        
//         // Wait for the image to load
//         await new Promise((resolve, reject) => {
//             img.onload = resolve;
//             img.onerror = reject;
//             img.crossOrigin = 'anonymous'; // Needed for CORS images
//             img.src = imageUrl;
//         });
        
//         // Create a canvas to compress the image
//         const canvas = document.createElement('canvas');
        
//         // Calculate new dimensions (max 600px width or height while preserving aspect ratio)
//         let width = img.width;
//         let height = img.height;
//         const maxDimension = 600;
        
//         if (width > height && width > maxDimension) {
//             height = Math.round(height * (maxDimension / width));
//             width = maxDimension;
//         } else if (height > maxDimension) {
//             width = Math.round(width * (maxDimension / height));
//             height = maxDimension;
//         }
        
//         // Set canvas dimensions
//         canvas.width = width;
//         canvas.height = height;
        
//         // Draw the image on the canvas with the new dimensions
//         const ctx = canvas.getContext('2d');
//         ctx.drawImage(img, 0, 0, width, height);
        
//         // Convert to compressed JPEG data URL (quality 0.8)
//         return canvas.toDataURL('image/jpeg', 0.8);
//     } catch (error) {
//         console.error('Error compressing external image:', error);
//         // Return original URL if compression fails
//         return imageUrl;
//     }
// }

// // Function to handle URLs that might have CORS issues
// function ensureCorsCompatibleUrl(url) {
//     // If it's already a data URL, return it as is
//     if (url.startsWith('data:')) {
//         return url;
//     }

//     // Always return the direct URL
//     return url;
// }

function pixelsToPoints(pixels) {
    return pixels * 0.75; // Assuming screen DPI is 96
}


function generateSlideRequests(canvasState, slideId) {
    console.log('Canvas State for Slide 0:', canvasState);
    console.log('Canvas State:', canvasState);
    console.log('Slide ID:', slideId);
    

    const canvasWidth = 375;
    const canvasHeight = 667;

    const slideWidth = 960;
    const slideHeight = 540;

    if (!canvasState || !Array.isArray(canvasState.objects)) {
        throw new Error('Invalid canvasState: objects property is missing or not an array');
    }

    const scaleWidth = slideWidth / canvasWidth;
    const scaleHeight = slideHeight / canvasHeight;
    const scale = Math.min(scaleWidth, scaleHeight);

    const xOffset = (slideWidth - canvasWidth * scale) / 2;
    const yOffset = (slideHeight - canvasHeight * scale) / 2;

    console.log('scale:', scale);
    console.log('xOffset:', xOffset);
    console.log('yOffset:', yOffset);

    const requests = [];

    canvasState.objects.forEach((obj, index) => {
        console.log(`Processing object ${index}:`, obj);

        const normalizedX = obj.left != null ? pixelsToPoints(obj.left * scale + xOffset) : 0;
        const normalizedY = obj.top != null ? pixelsToPoints(obj.top * scale + yOffset) : 0;
        const normalizedWidth = obj.width != null ? pixelsToPoints(obj.width * scale) : pixelsToPoints(100);
        const normalizedHeight = obj.height != null ? pixelsToPoints(obj.height * scale) : pixelsToPoints(100);

        const clampedWidth = Math.min(normalizedWidth, slideWidth);
        const clampedHeight = Math.min(normalizedHeight, slideHeight);
        const clampedX = Math.max(0, Math.min(normalizedX, slideWidth - clampedWidth));
        const clampedY = Math.max(0, Math.min(normalizedY, slideHeight - clampedHeight));

        console.log(`Normalized values for object ${index}:`, {
            clampedHeight,
            clampedWidth,
            clampedX,
            clampedY
        });

        if (obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox') {
            const fontSize = obj.fontSize || 12; // Default font size if not provided
            const textWidth = Math.max(obj.text.length * fontSize * 0.6, 100); // Ensure a minimum width of 100 PT
            const textHeight = Math.max(fontSize * 1.2, 20); // Ensure a minimum height based on font size
        
            requests.push({
                createShape: {
                    objectId: `obj_${slideId}_${index}`,
                    shapeType: 'TEXT_BOX',
                    elementProperties: {
                        pageObjectId: slideId,
                        size: {
                            width: { magnitude: textWidth, unit: 'PT' },
                            height: { magnitude: textHeight, unit: 'PT' }
                        },
                        transform: {
                            scaleX: 1,
                            scaleY: 1,
                            translateX: clampedX,
                            translateY: clampedY,
                            unit: 'PT'
                        }
                    }
                }
            });
        
            if (obj.text) {
                requests.push({
                    insertText: {
                        objectId: `obj_${slideId}_${index}`,
                        text: obj.text
                    }
                });
        
                requests.push({
                    updateTextStyle: {
                        objectId: `obj_${slideId}_${index}`,
                        textRange: { type: 'ALL' },
                        style: {
                            fontSize: { magnitude: fontSize, unit: 'PT' },
                            bold: obj.fontWeight === 'bold',
                            italic: obj.fontStyle === 'italic',
                            foregroundColor: {
                                opaqueColor: {
                                    rgbColor: parseRgb(obj.fill || '#000000')
                                }
                            }
                        },
                        fields: 'fontSize,bold,italic,foregroundColor'
                    }
                });
            }
        } else if (obj.type === 'triangle') {
            // Create a triangle shape
            requests.push({
                createShape: {
                    objectId: `obj_${slideId}_${index}`,
                    shapeType: 'TRIANGLE',
                    elementProperties: {
                        pageObjectId: slideId,
                        size: {
                            width: { magnitude: clampedWidth, unit: 'PT' },
                            height: { magnitude: clampedHeight, unit: 'PT' }
                        },
                        transform: {
                            scaleX: 1,
                            scaleY: 1,
                            translateX: clampedX,
                            translateY: clampedY,
                            unit: 'PT'
                        }
                    }
                }
            });

            // Set the fill color for the triangle
            requests.push({
                updateShapeProperties: {
                    objectId: `obj_${slideId}_${index}`,
                    fields: 'shapeBackgroundFill.solidFill.color',
                    shapeProperties: {
                        shapeBackgroundFill: {
                            solidFill: {
                                color: {
                                    rgbColor: parseRgb(obj.fill || '#000000')
                                }
                            }
                        }
                    }
                }
            });
        } else if (obj.type === 'image') {
            let imageUrl = obj.src;
            if (
                imageUrl.startsWith('http://localhost:3000/api/proxy/image') ||
                imageUrl.startsWith('https://www.datacreative.app/api/proxy/image')
            ) {
                const urlParams = new URLSearchParams(imageUrl.split('?')[1]);
                imageUrl = decodeURIComponent(urlParams.get('url'));
            }

            console.log('Using image URL:', imageUrl);

            requests.push({
                createImage: {
                    objectId: `obj_${slideId}_${index}`,
                    url: imageUrl,
                    elementProperties: {
                        pageObjectId: slideId,
                        size: {
                            width: { magnitude: clampedWidth, unit: 'PT' },
                            height: { magnitude: clampedHeight, unit: 'PT' }
                        },
                        transform: {
                            scaleX: 1,
                            scaleY: 1,
                            translateX: clampedX,
                            translateY: clampedY,
                            unit: 'PT'
                        }
                    }
                }
            });
        }
    });

    console.log('Generated requests:', requests);
    return requests;
}


// Helper: convert hex to Google Slides rgbColor object
function parseRgb(hex) {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              red: parseInt(result[1], 16) / 255,
              green: parseInt(result[2], 16) / 255,
              blue: parseInt(result[3], 16) / 255
          }
        : { red: 0, green: 0, blue: 0 };
}

/**
 * Handles Google authentication and ensures the user is signed in
 * @returns {Promise<boolean>} Whether the user is authenticated
 */
export async function ensureGoogleAuthentication() {
    if (await isSignedInToGoogle()) {
        console.log('User is already signed in to Google.');
        return true;
    }

    console.log('User is not signed in. Initiating sign-in process...');
    return await signInToGoogle();
}